<template>
    <UIModal class="wallet-rub-bank" padding="60" @close="onClose">
        <div class="wallet-rub-bank__content">
            <h2 class="wallet-rub-bank__title">{{ $t('wallet.rubWithdrawal') }}</h2>

            <div v-if="loading" class="wallet-rub-bank__loading">
                {{ $t('common.loading') }}
            </div>

            <template v-if="!loading">
                <div v-if="!hasBankDetails" class="wallet-rub-bank__warning">
                    {{ $t('wallet.noBankDetails') }}
                </div>

                <template v-else>
                    <div class="wallet-rub-bank__bank-card">
                        <p class="wallet-rub-bank__bank-name">{{ legal.bankName }}</p>
                        <p class="wallet-rub-bank__bank-account">{{ legal.bankAccount }}</p>
                        <p class="wallet-rub-bank__bank-legal">{{ legal.legalName }}, ИНН {{ legal.inn }}</p>
                    </div>

                    <p v-if="btcRub" class="wallet-rub-bank__rate">
                        {{ $t('wallet.currentRate') }}: {{ formatRub(btcRub) }} ₽/BTC
                    </p>

                    <div v-if="!submitted" class="wallet-rub-bank__form">
                        <div class="wallet-rub-bank__field">
                            <label class="wallet-rub-bank__label">{{ $t('wallet.transferAmount') }}, BTC</label>
                            <input
                                class="wallet-rub-bank__input"
                                type="number"
                                min="0"
                                step="0.00000001"
                                v-model="amount"
                                :disabled="formLoading"
                                placeholder="0.00000000"
                            />
                        </div>

                        <p v-if="rubEstimate" class="wallet-rub-bank__estimate">
                            ≈ {{ formatRub(rubEstimate) }} ₽
                        </p>

                        <p v-if="error" class="wallet-rub-bank__error">{{ error }}</p>

                        <button
                            class="wallet-rub-bank__btn"
                            :disabled="!canSubmit || formLoading"
                            @click="onSubmit"
                        >
                            {{ formLoading ? $t('common.loading') : $t('wallet.withdrawal') }}
                        </button>
                    </div>

                    <div v-if="submitted" class="wallet-rub-bank__success">
                        {{ $t('wallet.requestSent') }}
                    </div>
                </template>

                <div v-if="requests.length" class="wallet-rub-bank__history">
                    <h3 class="wallet-rub-bank__history-title">{{ $t('wallet.rubRequests') }}</h3>
                    <div v-for="req in requests" :key="req.id" class="wallet-rub-bank__request">
                        <span class="wallet-rub-bank__req-date">{{ formatDate(req.created) }}</span>
                        <span class="wallet-rub-bank__req-btc">{{ req.btcAmount }} BTC</span>
                        <span class="wallet-rub-bank__req-rub">{{ req.rubAmount ? '≈ ' + formatRub(req.rubAmount) + ' ₽' : '—' }}</span>
                        <span class="wallet-rub-bank__req-status" :class="'wallet-rub-bank__req-status--' + req.status.toLowerCase()">
                            {{ $t('wallet.status' + req.status) }}
                        </span>
                    </div>
                </div>
            </template>
        </div>
    </UIModal>
</template>

<script>
import UIModal from "@/components/ui/modal/modal.vue";
import {COMPONENTS} from "@/components/shared/wallet/content/content.vue";
import Api from "@/api/api";

const api = new Api();

export default {
    name: "WalletWithdrawRubBank",
    components: { UIModal },
    data() {
        return {
            loading: true,
            formLoading: false,
            submitted: false,
            amount: "",
            btcRub: null,
            legal: {},
            requests: [],
            error: null,
        };
    },
    async created() {
        try {
            const [rates, legal, requests] = await Promise.all([
                api.exchangeRates(),
                api.getLegal(),
                api.rubWithdrawals(),
            ]);
            if (rates && rates.length) {
                this.btcRub = rates[0].btcRub;
            }
            if (legal) this.legal = legal;
            if (requests) this.requests = requests;
        } catch {
            // non-critical, show form anyway
        } finally {
            this.loading = false;
        }
    },
    computed: {
        hasBankDetails() {
            return !!(this.legal.bankAccount && this.legal.bankName);
        },
        rubEstimate() {
            const amt = parseFloat(this.amount);
            if (!amt || !this.btcRub) return null;
            return amt * this.btcRub;
        },
        canSubmit() {
            const amt = parseFloat(this.amount);
            return this.hasBankDetails && amt > 0 && !this.submitted && !this.formLoading;
        },
    },
    methods: {
        formatRub(value) {
            return new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
        },
        formatDate(ts) {
            return new Date(ts).toLocaleDateString('ru-RU');
        },
        async onSubmit() {
            this.error = null;
            this.formLoading = true;
            try {
                const newReq = await api.createRubWithdrawal(this.amount);
                if (newReq) this.requests.unshift(newReq);
                this.submitted = true;
            } catch (e) {
                this.error = e?.response?.data?.message || "Ошибка при отправке заявки";
            } finally {
                this.formLoading = false;
            }
        },
        onClose() {
            this.$emit("close", COMPONENTS.default);
        },
    },
};
</script>

<style lang="scss">
.wallet-rub-bank {
    .wallet-rub-bank__content {
        width: 560px;
        padding: 42px 60px 56px 60px;
        display: flex;
        flex-direction: column;
        grid-row-gap: 20px;
    }

    .wallet-rub-bank__title {
        font-size: 24px;
        line-height: 29px;
        text-align: center;
        color: $txt-main;
    }

    .wallet-rub-bank__loading {
        text-align: center;
        color: $txt-light;
        font-size: 14px;
    }

    .wallet-rub-bank__warning {
        background: $bg-orange;
        border-radius: 10px;
        padding: 12px 16px;
        font-size: 14px;
        line-height: 1.5;
        color: $txt-main;
    }

    .wallet-rub-bank__bank-card {
        background: $bg-gray-light;
        border-radius: 10px;
        padding: 12px 16px;
        display: flex;
        flex-direction: column;
        grid-row-gap: 4px;
    }

    .wallet-rub-bank__bank-name {
        @include golos-medium;
        font-size: 14px;
        color: $txt-main;
    }

    .wallet-rub-bank__bank-account,
    .wallet-rub-bank__bank-legal {
        font-size: 13px;
        color: $txt-light;
    }

    .wallet-rub-bank__rate {
        font-size: 13px;
        color: $txt-light;
    }

    .wallet-rub-bank__form {
        display: flex;
        flex-direction: column;
        grid-row-gap: 12px;
    }

    .wallet-rub-bank__field {
        display: flex;
        flex-direction: column;
        grid-row-gap: 6px;
    }

    .wallet-rub-bank__label {
        @include golos-medium;
        font-size: 11px;
        color: $txt-light;
        text-transform: uppercase;
        letter-spacing: 0.05em;
    }

    .wallet-rub-bank__input {
        @include golos-regular;
        font-size: 14px;
        color: $txt-main;
        border: 1px solid $gray;
        border-radius: 10px;
        padding: 10px 14px;
        outline: none;
        background: $bg-light;
        width: 100%;
        box-sizing: border-box;
        transition: border-color $transition;

        &:focus {
            border-color: $main;
        }

        &:disabled {
            opacity: 0.5;
        }

        &::-webkit-inner-spin-button,
        &::-webkit-outer-spin-button {
            opacity: 1;
        }
    }

    .wallet-rub-bank__estimate {
        @include golos-medium;
        font-size: 18px;
        color: $txt-main;
        text-align: center;
    }

    .wallet-rub-bank__btn {
        @include button-reset;
        @include golos-bold;
        font-size: 14px;
        color: $white;
        background: $main;
        border-radius: 12px;
        padding: 14px 28px;
        cursor: pointer;
        transition: opacity $transition;
        width: 100%;

        &:disabled {
            opacity: 0.5;
            cursor: not-allowed;
        }

        &:hover:not(:disabled) {
            opacity: 0.85;
        }
    }

    .wallet-rub-bank__error {
        font-size: 13px;
        color: $status-red;
        text-align: center;
    }

    .wallet-rub-bank__success {
        background: $bg-green;
        border-radius: 10px;
        padding: 16px;
        font-size: 14px;
        text-align: center;
        color: $txt-main;
    }

    .wallet-rub-bank__history {
        border-top: 1px solid $bg-gray;
        padding-top: 16px;
        display: flex;
        flex-direction: column;
        grid-row-gap: 8px;
    }

    .wallet-rub-bank__history-title {
        font-size: 16px;
        color: $txt-main;
        @include golos-medium;
    }

    .wallet-rub-bank__request {
        display: grid;
        grid-template-columns: 90px 1fr 1fr auto;
        align-items: center;
        gap: 8px;
        font-size: 13px;
        color: $txt-main;
    }

    .wallet-rub-bank__req-date {
        color: $txt-light;
    }

    .wallet-rub-bank__req-status {
        @include golos-medium;
        font-size: 11px;
        text-transform: uppercase;
        padding: 3px 8px;
        border-radius: 6px;
        white-space: nowrap;

        &--pending {
            background: $bg-orange;
        }

        &--processing {
            background: $bg-purple;
        }

        &--completed {
            background: $bg-green;
        }

        &--failed {
            background: $bg-gray;
            color: $txt-light;
        }
    }
}

@media screen and (max-width: 768px) {
    .wallet-rub-bank {
        .wallet-rub-bank__content {
            width: 340px;
            padding: 28px 16px 32px 16px;
            grid-row-gap: 16px;
        }

        .wallet-rub-bank__request {
            grid-template-columns: 80px 1fr;
            grid-template-rows: auto auto;
        }
    }
}
</style>
